import { useState, useEffect, useCallback, useMemo } from "react";
import { Button, Nav, Tab } from "react-bootstrap";
import { omit } from "lodash";
// components
import ModalLayout from "components/modal/modal-layout/ModalLayout";
import DiagnosisFields from "../diagnosis-fields";
import GoalFields from "../goal-fields";
import ConfirmModal from "components/confirm-modal";
import ReminderFields from "../reminder-fields/ReminderFields";
import BehaviorFields from "../behavior-fields";
// constants
import messages from "messages";
// styles
import styles from "./styles.module.scss";
// hooks
import useToggleModal from "hooks/useToggleModal";
// utils
import getGoalsValidationError from "./utils/getGoalsValidationError";
import getValidGoals from "./utils/getValidGoals";

export const STEPS = Object.freeze({
  diagnosis: {
    id: "diagnosis",
    label: messages.diagnosis,
  },
  behaviors: {
    id: "behaviors",
    label: messages.behavior,
  },
  goals: {
    id: "goals",
    label: messages.goal,
  },
  reminder: {
    id: "reminder",
    label: messages.reminder,
  },

  overview: {
    id: "overview",
    label: messages.overview,
  },
} as const);

/**
 * step orders of the sidebar
 */
const ORDERS = Object.keys(STEPS);

/**
 * goal step order
 */
const GOALS_ORDER = ORDERS.indexOf("goals");

type Props = {
  code: ICDCode & { id: string; [key: string]: any };
  value: AdvancedTreatmentPlan;
  show: boolean;
  onApply: (arg: AdvancedTreatmentPlan) => void;
  onCancel: () => void;
};

const AdvancedPlanEditor = ({
  code,
  value,
  show,
  onApply,
  onCancel,
}: Props) => {
  const [localValue, setLocalValue] = useState(value);
  const hasValue = useMemo(
    () => Object.keys(code.treatment_plan || {}).length > 1,
    [code.treatment_plan],
  );
  useEffect(() => setLocalValue(value), [value]);

  const [step, setStep] = useState(
    hasValue ? ORDERS.indexOf(STEPS.overview.id) : 0,
  );

  // cancel editor modal
  const {
    open: openPlanModal,
    show: showPlanModal,
    close: closePlanModal,
  } = useToggleModal(false);

  // skip to the end modal
  const {
    open: openSkipModal,
    show: showSkipModal,
    close: closeSkipModal,
  } = useToggleModal(false);

  const [skipModalProps, setSkipModalProps] = useState<{
    children: string;
    labelCancel: string;
  }>({
    children: "",
    labelCancel: "",
  });
  const planModalProps = useMemo(() => {
    if (!hasValue) {
      return {
        children: messages.ifYouCancelTreatment,
        labelConfirm: messages.cancel,
        labelCancel: messages.dontCancel,
      };
    }
    return {
      children: messages.ifYouClose,
      labelConfirm: messages.continue,
      labelCancel: messages.keepEditing,
    };
  }, [hasValue]);

  const handleApply = useCallback(() => {
    if (step === ORDERS.length - 1) {
      return onApply(localValue);
    }

    if (step === GOALS_ORDER) {
      const error = getGoalsValidationError(localValue);
      if (error) {
        setSkipModalProps(error);
        return openSkipModal();
      }
    }

    // clean up the goals
    setLocalValue((s) => getValidGoals(s));
    return setStep((s) => s + 1);
  }, [step, setStep, localValue, openSkipModal, onApply]);

  const handleCancel = useCallback(
    (clickClose?: boolean) => {
      const noChange = JSON.stringify(value) === JSON.stringify(localValue);

      if (clickClose) {
        if (hasValue && noChange) {
          return onCancel();
        }

        return openPlanModal();
      }

      if (step === ORDERS.length - 1) {
        return openPlanModal();
      }

      return setStep((s) => s - 1);
    },
    [value, localValue, hasValue, step, setStep, openPlanModal, onCancel],
  );

  return (
    <>
      {/* plan modal */}
      <ConfirmModal
        labelTitle={messages.areYouSure}
        show={showPlanModal}
        onCancel={closePlanModal}
        onConfirm={() => {
          closePlanModal();
          onCancel();
        }}
        {...planModalProps}
      />
      {/* skip to the end */}
      <ConfirmModal
        labelTitle={messages.skipToTheEnd}
        labelConfirm={messages.yesSkipToTheEnd}
        show={showSkipModal}
        onCancel={closeSkipModal}
        children={skipModalProps.children}
        labelCancel={skipModalProps.labelCancel}
        onConfirm={() => {
          if (skipModalProps.children === messages.youMustCreateGoal1) {
            setLocalValue((s) => omit(s, STEPS.goals.id));
          } else {
            setLocalValue((s) => getValidGoals(s));
          }
          closeSkipModal();
          setStep(ORDERS.length - 1);
        }}
      />
      <ModalLayout
        dialogClassName={styles["advanced-plan-editor__dialog"]}
        backdrop={showSkipModal ? false : "static"}
        keyboard={false}
        size={"lg"}
        show={show}
        onHide={() => handleCancel(true)}
        Header={
          <span className={styles["advanced-plan-editor__title"]}>
            {hasValue ? messages.editTreatmentPlan : messages.addTreatmentPlan}
          </span>
        }
        headerClass={styles["advanced-plan-editor__header"]}
        bodyClass={styles["advanced-plan-editor__body"]}
        Footer={
          <>
            <Button
              className={styles["advanced-plan-editor__cancel"]}
              size="sm"
              onClick={() => handleCancel()}
              variant={"outline-primary"}
              disabled={step === 0}
            >
              {step === ORDERS.length - 1 ? messages.cancel : messages.goBack}
            </Button>

            <Button
              className={styles["advanced-plan-editor__apply"]}
              size="sm"
              onClick={handleApply}
            >
              {step === ORDERS.length - 1
                ? hasValue
                  ? messages.updatePlan
                  : messages.completeAndSave
                : messages.next}
            </Button>
          </>
        }
        footerClass={styles["advanced-plan-editor__footer"]}
      >
        <Tab.Container activeKey={step}>
          <Nav className={styles["advanced-plan-editor__sidebar"]}>
            {Object.entries(STEPS).map(([key, stepValue], index) => (
              <Nav.Item
                key={key}
                className={styles["advanced-plan-editor__nav-item"]}
              >
                <Nav.Link
                  eventKey={index}
                  className={styles["advanced-plan-editor__nav-link"]}
                  disabled={index !== step}
                >
                  {`${index + 1}. ${stepValue.label}`}
                </Nav.Link>
              </Nav.Item>
            ))}
          </Nav>
          <div
            className={styles["advanced-plan-editor__tab-content-container"]}
          >
            <Tab.Content>
              {ORDERS.map((key, index) => (
                <Tab.Pane eventKey={index} key={key}>
                  {(index === 0 || index === ORDERS.length - 1) && (
                    <DiagnosisFields
                      className={styles["advanced-plan-editor__block"]}
                      readOnly={false}
                      code={code}
                      value={localValue}
                      setValue={setLocalValue}
                    />
                  )}
                  {(index === 1 || index === ORDERS.length - 1) && (
                    <BehaviorFields
                      className={styles["advanced-plan-editor__block"]}
                      readOnly={false}
                      value={localValue}
                      setValue={setLocalValue}
                    />
                  )}
                  {(index === 2 ||
                    (index === ORDERS.length - 1 && localValue.goals)) && (
                    <GoalFields
                      className={styles["advanced-plan-editor__block"]}
                      readOnly={false}
                      value={localValue}
                      setValue={setLocalValue}
                    />
                  )}
                  {(index === 3 || index === ORDERS.length - 1) && (
                    <ReminderFields
                      className={styles["advanced-plan-editor__block"]}
                      readOnly={false}
                      value={localValue}
                      setValue={setLocalValue}
                    />
                  )}
                </Tab.Pane>
              ))}
            </Tab.Content>
          </div>
        </Tab.Container>
      </ModalLayout>
    </>
  );
};

export default AdvancedPlanEditor;
